import React from 'react';
import { etapaColor } from '../../utils/estados';
import AvatarTecnico from '../ui/AvatarTecnico';
import { fueOtraCosa, corto } from '../../utils/pedidoVsHecho';
import { tituloSinCliente } from '../../utils/ordenes';

const ESTADO = { PENDIENTE: 'Agendado', EN_CAMINO: 'En camino', EN_SITIO: 'Trabajando', COMPLETADA: 'Hecho', NO_ATENDIDO: 'No atendido' };
const primer = (n) => String(n || '').split(' ')[0];
const lista = (ns) => (ns.length > 1 ? `${ns.slice(0, -1).join(', ')} y ${ns.at(-1)}` : ns[0] || '');

const calle = (d) => String(d || '').split(',')[0].trim();
const horaDe = (iso) => { const m = String(iso || '').match(/T(\d{2}:\d{2})/); return m ? m[1] : null; };

/**
 * Una visita de la agenda (tocable si hay onAbrir). 27-oct-2026: antes decía "Modo Agua · 8 a 10 h · hecho"
 * en letra chica, igual para todas. Ahora va adelante a quién (el lugar del pedido), abajo empresa, motivo y
 * calle, y a la derecha el estado bien marcado: lo hecho en verde con la hora en que se cerró.
 */
export function VisitaAgenda({ o, conAvatar, onAbrir }) {
    const hecha = o.estado === 'COMPLETADA';
    const tentativa = o.sinConfirmar && o.estado === 'PENDIENTE';
    const quien = o.lugar || o.clienteNombre || o.titulo || `Visita #${o.id}`;
    const otra = hecha && fueOtraCosa(o.motivo, o.hecho);
    const trabajo = hecha && o.hecho ? `${corto(o.hecho)}${otra ? ` (pidieron ${String(o.motivo).toLowerCase()})` : ''}` : o.motivo || (o.titulo && o.titulo !== quien ? tituloSinCliente(o.titulo, quien) : null);
    const detalle = [o.lugar ? o.clienteNombre : null, trabajo, calle(o.direccion)]
        .filter(Boolean).join(' · ');
    const estado = tentativa ? (o.noPuedeEn ? 'Tentativa · no puede' : o.confirmadaEn ? 'Tentativa · puede' : 'Tentativa')
        : hecha ? `✓ Hecho${horaDe(o.fechaCompletada) ? ` ${horaDe(o.fechaCompletada)}` : ''}` : ESTADO[o.estado] || o.estado;
    const color = hecha ? 'var(--etapa-listo)' : etapaColor(o.estado);
    const cuerpo = (
        <>
            {conAvatar && <AvatarTecnico nombre={o.tecnicoNombre} size={30} />}
            <span className="flex-1 min-w-0">
                <span className={`block text-body-lg font-black truncate ${hecha ? 'text-secondary' : 'text-ink'}`}>{quien}</span>
                {detalle && <span className="block text-body text-muted truncate">{detalle}</span>}
                {/* En el celu el estado va abajo, para que el nombre no quede cortado */}
                <span className="sm:hidden mt-1 h-6 px-2 rounded-md text-caption font-black inline-flex items-center"
                    style={{ color, background: `color-mix(in srgb, ${color} 15%, transparent)` }}>{estado}</span>
            </span>
            <span className="hidden sm:inline-flex shrink-0 h-8 px-2.5 rounded-lg text-body font-black items-center"
                style={{ color, background: `color-mix(in srgb, ${color} 15%, transparent)` }}>{estado}</span>
        </>
    );
    const cls = `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left border-l-4 ${hecha ? 'bg-page/60' : 'bg-page'}`;
    return onAbrir
        ? <button type="button" onClick={() => onAbrir(o)} className={`${cls} active:scale-[0.99] hover:bg-chip`} style={{ borderLeftColor: color }}>{cuerpo}</button>
        : <div className={cls} style={{ borderLeftColor: color }}>{cuerpo}</div>;
}

/**
 * Una franja del día (20-oct-2026). Con un técnico: sus visitas, "No disponible" si marcó trabajo
 * propio y el hueco para tocar. Con "Todos": las visitas de todos y quién está libre.
 */
export default function FranjaFila({ franja, visitas, tecnico, todos, ocupados, libres, elegir, on, pasada, zona, onElegir, onAbrir, onMarcar = null, onQuitar = null, aviso = false }) {
    const ocupado = !todos && ocupados.length > 0;
    return (
        <div className="flex gap-2.5">
            <div className="w-14 shrink-0 pt-2">
                <p className="text-body font-black text-ink leading-tight">{franja.corto}</p>
            </div>
            <div className="flex-1 min-w-0 space-y-1.5">
                {visitas.map(o => <VisitaAgenda key={o.id} o={o} conAvatar={todos} onAbrir={onAbrir} />)}
                {ocupado ? (
                    <div className="franja-ocupada px-3 py-2 rounded-xl flex items-center gap-2">
                        <span className="flex-1 min-w-0">
                            <span className="block text-caption font-black text-secondary">No disponible</span>
                            <span className="block text-label text-muted">{primer(tecnico?.nombre)}: trabajo propio</span>
                        </span>
                        {onQuitar && <button type="button" onClick={onQuitar} className="shrink-0 h-8 px-2.5 rounded-lg bg-card text-label font-black text-secondary active:scale-95">Quitar</button>}
                    </div>
                ) : todos ? (
                    (libres.length > 0 || ocupados.length > 0) && (
                        <p className="px-1 text-label text-muted">
                            {libres.length > 0 && <>Libre{libres.length > 1 ? 's' : ''}: {lista(libres.map(primer))}</>}
                            {libres.length > 0 && ocupados.length > 0 && ' · '}
                            {ocupados.length > 0 && <>No disponible: {lista(ocupados.map(primer))}</>}
                        </p>
                    )
                ) : elegir && !(pasada && !on) ? (
                    <button type="button" onClick={onElegir} aria-pressed={on}
                        className={`w-full min-h-11 px-3 py-2 rounded-xl text-left text-caption font-black active:scale-[0.99] ${on
                            ? 'border-2 border-brand-red bg-[rgba(232,66,47,0.10)] text-brand-red'
                            : aviso ? 'border-2 border-[#D97706] bg-[rgba(217,119,6,0.10)] text-[#A16207] dark:text-[#F0A500]'
                            : 'border-2 border-dashed border-black/15 dark:border-white/15 text-secondary'}`}>
                        {/* 26-oct-2026: ocupada no se pisa sin avisar */}
                        {on ? (visitas.length ? `✓ Este trabajo · se superpone con ${visitas.map(o => o.clienteNombre || o.titulo).join(', ')}` : '✓ Este trabajo')
                            : aviso ? `${primer(tecnico?.nombre)} ya tiene ${visitas.map(o => o.clienteNombre || o.titulo).join(', ')} de ${franja.corto}. Tocá otra vez para sumarla igual`
                            : visitas.length ? 'Ocupada · sumar igual' : '+ Libre'}
                        {zona && <span className="block text-label font-black text-[#16A34A] dark:text-[#4ADE80]">Ya va por esa zona</span>}
                    </button>
                ) : (
                    <div className="flex items-center gap-2 px-1 pt-1">
                        {!visitas.length && <span className="flex-1 text-label text-muted">{pasada ? 'Ya pasó' : 'Libre'}</span>}
                        {visitas.length > 0 && <span className="flex-1" />}
                        {onMarcar && <button type="button" onClick={onMarcar} className="shrink-0 h-8 px-2.5 rounded-lg text-label font-bold text-muted underline underline-offset-2 active:scale-95">Marcar no disponible</button>}
                    </div>
                )}
            </div>
        </div>
    );
}
